import styled from '@emotion/styled';
import { Typography } from '@mui/material';
import Image from 'next/image';
import { useAuth0 } from '../../../auth';
import { theme } from '@/app/common/providers/theme-provider/theme';
import { MidnightButton, TextButton } from '../../button/button';
import { MediaContainer } from '../../media-container/media-container';
import { Modal, ModalProps } from '../../modal/modal';
import Blob from '../svgs/create-experience.svg';
import { nextImageTransitionProps } from '@/constants/next-image-transition-props';

const Title = styled(Typography)`
  font-family: var(--font-cooper-light), sans-serif;
  font-size: 30px;
  line-height: 1.2;
  text-align: center;
  margin-bottom: 20px;

  ${theme.breakpoints.up('sm')} {
    font-size: 40px;
    margin-bottom: 40px;
  }
`;

const Content = styled.div`
  padding: 50px 20px 80px;
  max-width: 650px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 3;

  ${theme.breakpoints.up('sm')} {
    padding: 50px 50px 150px;
  }
`;

const Momenteer = styled(MediaContainer)`
  display: none;
  pointer-events: none;
  width: 269px;
  height: 392px;
  position: absolute;
  left: 0;
  bottom: 0;
  z-index: 2;

  ${theme.breakpoints.up('md')} {
    display: block;
  }
`;

const Background = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 0;
  padding-bottom: 21.3483%;
  z-index: 1;
  overflow: hidden;
  pointer-events: none;

  svg {
    position: absolute;
    bottom: -2px;
    left: -2px;
    right: -2px;
    height: 100%;
  }
`;

const LoginButton = styled(TextButton)`
  font-size: 1rem;
  margin: 1rem 0;
  padding: 4px;
  text-align: center;

  &:hover {
    text-decoration: underline;
  }
`;

export type UnauthedModalProps = Omit<ModalProps, 'children'> & {
  onLogin: () => void;
};

export const UnauthedModal = ({ onLogin, ...props }: UnauthedModalProps): React.ReactElement => {
  const { isLoading, loginWithRedirect } = useAuth0();
  return (
    <Modal maxWidth="md" fullWidth scroll="body" {...props}>
      <Content>
        <Title variant="h3">You&apos;ll need a Curiko account to create an experience!</Title>
        <MidnightButton
          onClick={() =>
            loginWithRedirect({
              screen_hint: 'signup',
            }).then(onLogin)
          }
          loading={isLoading}
        >
          {isLoading ? 'Loading...' : 'Sign up'}
        </MidnightButton>
        <LoginButton onClick={() => loginWithRedirect().then(onLogin)} disabled={isLoading}>
          Already have an account? Login here
        </LoginButton>
      </Content>
      <Background>
        <Blob />
      </Background>
      <Momenteer>
        <Image src="/login-modal/login-modal-momenteer.png" alt="" {...nextImageTransitionProps} />
      </Momenteer>
    </Modal>
  );
};
